/** Turning streamed reply text into spoken sentences (sentence splitting ported from vrtest/brain). */

const BOUNDARY = /[.!?…]+["')\]]*(?=\s)|\n+/g;

export class SentenceSplitter {
  private buf = "";
  private emitted = false;

  constructor(
    private minChars = 20,
    private firstMinChars = 6,
  ) {}

  feed(text: string): string[] {
    this.buf += text;
    const out: string[] = [];
    let start = 0;
    BOUNDARY.lastIndex = 0;
    for (let m = BOUNDARY.exec(this.buf); m; m = BOUNDARY.exec(this.buf)) {
      const end = m.index + m[0].length;
      const needed = this.emitted || out.length ? this.minChars : this.firstMinChars;
      if (this.buf.slice(start, end).trim().length >= needed) {
        out.push(this.buf.slice(start, end).trim());
        start = end;
      }
    }
    this.emitted = this.emitted || out.length > 0;
    this.buf = this.buf.slice(start);
    return out;
  }

  flush(): string[] {
    const rest = this.buf.trim();
    this.buf = "";
    return rest ? [rest] : [];
  }
}

/** Text worth reading aloud: no code blocks, links reduced to their labels. */
export function speakable(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?(```|$)/g, " ")
    .replace(/<think>[\s\S]*?(<\/think>|$)/g, " ")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/\S+/g, " ");
}

/** Plays sentences one after another, fetching the next one's audio while the current one plays. */
export class SpeechPlayer {
  private ctx: AudioContext | null = null;
  private queue: string[] = [];
  private pending: Promise<AudioBuffer | null> | null = null;
  private source: AudioBufferSourceNode | null = null;
  private running = false;
  private generation = 0;
  onState?: (speaking: boolean) => void;
  onError?: (message: string) => void;

  constructor(private voice: () => string | undefined) {}

  get speaking() {
    return this.running;
  }

  private audio(): AudioContext {
    this.ctx ??= new AudioContext();
    if (this.ctx.state === "suspended") this.ctx.resume().catch(() => undefined);
    return this.ctx;
  }

  private async fetchAudio(text: string, gen: number): Promise<AudioBuffer | null> {
    try {
      const r = await fetch("/api/voice/speak", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, voice: this.voice() }) });
      if (!r.ok) {
        const msg = await r.json().then((j) => j.detail).catch(() => r.statusText);
        if (gen === this.generation) this.onError?.(String(msg));
        return null;
      }
      const data = await r.arrayBuffer();
      if (!data.byteLength || gen !== this.generation) return null;
      return await this.audio().decodeAudioData(data);
    } catch {
      return null;
    }
  }

  say(sentence: string) {
    if (!/[\p{L}\p{N}]/u.test(sentence)) return;
    this.queue.push(sentence);
    if (!this.running) void this.run();
  }

  stop() {
    this.generation += 1;
    this.queue = [];
    this.pending = null;
    try {
      this.source?.stop();
    } catch {
      /* already stopped */
    }
    this.source = null;
    if (this.running) {
      this.running = false;
      this.onState?.(false);
    }
  }

  private async run() {
    const gen = this.generation;
    this.running = true;
    this.onState?.(true);
    while (gen === this.generation) {
      const next = this.pending ?? (this.queue.length ? this.fetchAudio(this.queue.shift()!, gen) : null);
      this.pending = null;
      if (!next) break;
      const buf = await next;
      if (gen !== this.generation) return;
      // Start fetching the following sentence while this one plays.
      if (this.queue.length) this.pending = this.fetchAudio(this.queue.shift()!, gen);
      if (buf) await this.play(buf);
    }
    if (gen === this.generation) {
      this.running = false;
      this.onState?.(false);
    }
  }

  private play(buf: AudioBuffer): Promise<void> {
    return new Promise((resolve) => {
      const ctx = this.audio();
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.onended = () => resolve();
      this.source = src;
      src.start();
    });
  }
}
